/**
 * DateDisplay Component
 *
 * The input/trigger area for the date picker.
 * Shows the current value (or semantic label) and indicates state.
 */

import React from 'react'
import { CalendarOutlined, DownOutlined } from '@ant-design/icons'
import type { DateDisplayProps } from '../types'

/**
 * Renders the input display area for the semantic date picker.
 *
 * Shows:
 * - Calendar icon on left
 * - Date value OR semantic label in center
 * - Dropdown arrow on right
 *
 * Visual states:
 * - Has value: Black text with formatted date
 * - Has semantic null: Blue text with semantic label
 * - Empty/placeholder: Gray text
 * - Disabled: Grayed out, not clickable
 * - Open: Highlighted border
 */
export const DateDisplay: React.FC<DateDisplayProps> = ({
  value,
  pendingValue,
  wantsSemanticNull,
  semanticConfig,
  format,
  timeFormat,
  showTime,
  disabled,
  isOpen,
  placeholder,
  timezoneAbbreviation,
  onClick,
}) => {
  // Determine what to display
  const getDisplayText = (): { text: string; isSemanticNull: boolean; isEmpty: boolean } => {
    // If user has selected semantic null, show that
    if (wantsSemanticNull && semanticConfig) {
      return { text: semanticConfig.displayLabel, isSemanticNull: true, isEmpty: false }
    }

    // If there's a pending value, show that (preview)
    if (pendingValue) {
      const fullFormat = showTime && timeFormat ? `${format} ${timeFormat}` : format
      const formatted = pendingValue.format(fullFormat)
      const withTz = timezoneAbbreviation ? `${formatted} ${timezoneAbbreviation}` : formatted
      return { text: withTz, isSemanticNull: false, isEmpty: false }
    }

    // If there's a committed value, show that
    if (value) {
      const fullFormat = showTime && timeFormat ? `${format} ${timeFormat}` : format
      const formatted = value.format(fullFormat)
      const withTz = timezoneAbbreviation ? `${formatted} ${timezoneAbbreviation}` : formatted
      return { text: withTz, isSemanticNull: false, isEmpty: false }
    }

    // If no value but has semantics, show semantic label
    if (semanticConfig && !value) {
      return { text: semanticConfig.displayLabel, isSemanticNull: true, isEmpty: false }
    }

    // Empty - show placeholder
    return { text: placeholder, isSemanticNull: false, isEmpty: true }
  }

  const { text, isSemanticNull, isEmpty } = getDisplayText()

  // Build class names
  const classNames = ['semantic-date-display']
  if (disabled) classNames.push('semantic-date-display--disabled')
  if (isOpen) classNames.push('semantic-date-display--open')
  if (isSemanticNull) classNames.push('semantic-date-display--semantic')
  if (isEmpty) classNames.push('semantic-date-display--empty')

  // Build aria-label for screen readers
  const ariaLabel = isEmpty
    ? `Date picker: ${placeholder}`
    : isSemanticNull
      ? `Date picker: ${text} (semantic value)`
      : `Date picker: ${text}`

  return (
    <div
      className={classNames.join(' ')}
      onClick={disabled ? undefined : onClick}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-disabled={disabled}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
    >
      <CalendarOutlined className="semantic-date-display__icon" />
      <span className="semantic-date-display__text">{text}</span>
      <DownOutlined
        className={`semantic-date-display__arrow ${isOpen ? 'semantic-date-display__arrow--open' : ''}`}
      />
    </div>
  )
}
